import React, { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Box,
  Typography,
  Paper,
  Divider,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Stack,
  Avatar,
} from '@mui/material'
import { Delete, Add } from '@mui/icons-material'
import SegmentFields from './SegmentFields'
import { getIcon } from '../NavigationItem'

export interface SegmentInspectorProps {
  segment: any
  segmentIndex: number
  loopIndex: number
  onChange: (data: any) => void
  onNavigateToElement?: (element: any, elementIndex: number) => void
  readonly?: boolean
}

const SegmentInspector = React.memo<SegmentInspectorProps>(
  ({ segment, segmentIndex, loopIndex, onChange, onNavigateToElement, readonly = false }) => {
    const [localSegment, setLocalSegment] = useState(segment)

    useEffect(() => {
      setLocalSegment(segment)
    }, [segment])

    // Sort elements by position ascending
    const sortedElements = useMemo(() => {
      if (!localSegment.elements) return []
      return [...localSegment.elements].sort((a, b) => {
        const posA = a.position || 0
        const posB = b.position || 0
        return posA - posB
      })
    }, [localSegment.elements])

    const handleFieldChange = useCallback(
      (field: string, value: any) => {
        const updatedSegment = {
          ...localSegment,
          [field]: value,
        }
        setLocalSegment(updatedSegment)
        onChange(updatedSegment)
      },
      [localSegment, onChange]
    )

    const handleElementChange = useCallback(
      (elementIndex: number, field: string, value: any) => {
        const updatedElements =
          localSegment.elements?.map((element: any, index: number) =>
            index === elementIndex ? { ...element, [field]: value } : element
          ) || []

        const updatedSegment = {
          ...localSegment,
          elements: updatedElements,
        }
        setLocalSegment(updatedSegment)
        onChange(updatedSegment)
      },
      [localSegment, onChange]
    )

    const handleAddElement = useCallback(() => {
      const newElement = {
        id: `element-${Date.now()}`,
        name: 'New Element',
        usage: 'M',
        data_type: 'string',
        min_length: 1,
        max_length: 10,
      }

      const updatedElements = [...(localSegment.elements || []), newElement]
      const updatedSegment = {
        ...localSegment,
        elements: updatedElements,
      }
      setLocalSegment(updatedSegment)
      onChange(updatedSegment)
    }, [localSegment, onChange])

    const handleRemoveElement = useCallback(
      (elementIndex: number) => {
        const updatedElements =
          localSegment.elements?.filter((_: any, index: number) => index !== elementIndex) || []
        const updatedSegment = {
          ...localSegment,
          elements: updatedElements,
        }
        setLocalSegment(updatedSegment)
        onChange(updatedSegment)
      },
      [localSegment, onChange]
    )

    if (!localSegment) {
      return (
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            No segment data available
          </Typography>
        </Box>
      )
    }

    return (
      <Box sx={{ p: 2 }}>
        {/* Segment Properties */}
        <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom fontWeight={'bold'} pb={2}>
            Segment Properties
          </Typography>
          <SegmentFields
            segment={localSegment}
            readonly={readonly}
            onFieldChange={handleFieldChange}
            showId={true}
            showName={true}
            showUsage={true}
            showMaxUse={true}
          />
        </Paper>

        {/* Elements Section */}
        <Paper elevation={1} sx={{ p: 2 }}>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {getIcon('element')}
              <Typography variant="h6" fontWeight={'bold'}>
                Elements
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ({sortedElements.length || 0})
              </Typography>
            </Box>
            {!readonly && (
              <Button
                startIcon={<Add />}
                onClick={handleAddElement}
                size="small"
                variant="outlined"
              >
                Add Element
              </Button>
            )}
          </Box>
          <Divider sx={{ mb: 2 }} />

          {sortedElements && sortedElements.length > 0 ? (
            <List dense>
              {sortedElements.map((element: any, index: number) => (
                <ListItem
                  key={index}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                    cursor: onNavigateToElement ? 'pointer' : 'default',
                    '&:hover': onNavigateToElement
                      ? {
                          backgroundColor: 'action.hover',
                        }
                      : {},
                  }}
                  onClick={
                    onNavigateToElement ? () => onNavigateToElement(element, index) : undefined
                  }
                >
                  <ListItemText
                    primary={
                      <Box>
                        <Typography variant="subtitle1" component="div" sx={{ fontWeight: 500 }}>
                          {element.name || `Element ${index + 1}`}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }} alignItems="center">
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              bgcolor: 'primary.main',
                              fontSize: '0.875rem',
                              fontWeight: 'bold',
                            }}
                          >
                            {element.position || index + 1}
                          </Avatar>
                          <Chip
                            label={`Usage: ${element.usage || 'M'}`}
                            size="small"
                            color={element.usage === 'M' ? 'error' : 'default'}
                            variant="outlined"
                          />
                          <Chip
                            label={`Type: ${element.data_type || 'string'}`}
                            size="small"
                            color="secondary"
                            variant="outlined"
                          />
                          <Chip
                            label={`Length: ${element.min_length || 0}-${element.max_length || 0}`}
                            size="small"
                            color="info"
                            variant="outlined"
                          />
                        </Stack>
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    {!readonly && (
                      <IconButton
                        edge="end"
                        onClick={e => {
                          e.stopPropagation()
                          handleRemoveElement(index)
                        }}
                        size="small"
                      >
                        <Delete />
                      </IconButton>
                    )}
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              No elements defined
            </Typography>
          )}
        </Paper>
      </Box>
    )
  }
)

SegmentInspector.displayName = 'SegmentInspector'

export default SegmentInspector
